import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Alert, Skeleton, Snackbar, ThemeProvider } from '@mui/material';
import { deleteProduct, listBrands, listProducts, reorderProducts } from '../../api';
import { reorderList } from '../../utils/reorderList';
import { blackIconTheme } from '../../theme/blackIconTheme';
import ProductCard from './components/ProductCard';
import ProductActions from './components/ProductActions';
import ProductsToolbar from './components/ProductsToolbar';
import Pager from './components/Pager';
import DragHandle from './components/DragHandle';
import ReorderControls from './components/ReorderControls';
import s from './ProductsPage.module.scss';

function uniqueIds(ids = []) {
    const seen = new Set();
    const out = [];

    ids.forEach((id) => {
        const value = String(id || '');
        if (!value || seen.has(value)) return;
        seen.add(value);
        out.push(value);
    });

    return out;
}

function buildBrandOrder(fullIds, visibleBeforeIds, visibleAfterIds) {
    const beforeSet = new Set(visibleBeforeIds);
    const afterUnique = uniqueIds(visibleAfterIds);
    const allUnique = uniqueIds(fullIds);

    if (
        afterUnique.length !== visibleAfterIds.length
        || allUnique.length !== fullIds.length
    ) {
        throw new Error('Duplicate product IDs detected during reorder.');
    }

    let cursor = 0;
    const nextIds = fullIds.map((id) => {
        if (!beforeSet.has(id)) return id;
        const replacement = afterUnique[cursor];
        cursor += 1;
        return replacement;
    });

    const nextUnique = uniqueIds(nextIds);
    if (nextUnique.length !== nextIds.length) {
        throw new Error('Duplicate product IDs detected in final order.');
    }

    return nextIds;
}

async function listAllBrandProducts(brandId) {
    const pageSize = 200;
    let page = 1;
    let total = Number.POSITIVE_INFINITY;
    const items = [];

    while (items.length < total) {
        const response = await listProducts({
            page,
            limit: pageSize,
            brandId,
            sort: 'order',
            dir: 'asc',
        });

        const chunk = Array.isArray(response?.items) ? response.items : [];
        if (!chunk.length) break;

        items.push(...chunk);
        total = Number(response?.total ?? items.length);

        if (chunk.length < pageSize) break;
        page += 1;
    }

    return items;
}

export default function ProductsPage() {
    const [items, setItems] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });

    const [brandId, setBrandId] = useState('');
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [availableOnly, setAvailableOnly] = useState(false);

    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState('createdAt');
    const [dir, setDir] = useState('desc');

    const [savingOrder, setSavingOrder] = useState(false);
    const [autosave, setAutosave] = useState('idle');

    const limit = 24;
    const reorderEnabled = Boolean(brandId);

    const brandById = useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);
    const pages = Math.max(Math.ceil(total / limit), 1);

    const autosaveTimeoutRef = useRef(null);
    const debounceSaveRef = useRef(null);
    const rollbackItemsRef = useRef([]);
    const pendingReorderRef = useRef(null);

    const clearAutosaveTimer = useCallback(() => {
        if (autosaveTimeoutRef.current) {
            clearTimeout(autosaveTimeoutRef.current);
            autosaveTimeoutRef.current = null;
        }
    }, []);

    const clearDebounceTimer = useCallback(() => {
        if (debounceSaveRef.current) {
            clearTimeout(debounceSaveRef.current);
            debounceSaveRef.current = null;
        }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedQuery(query);
            setPage(1);
        }, 350);

        return () => clearTimeout(t);
    }, [query]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setErr('');

            const [prods, brs] = await Promise.all([
                listProducts({
                    page,
                    limit,
                    sort: brandId ? 'order' : sort,
                    dir: brandId ? 'asc' : dir,
                    brandId: brandId || undefined,
                    q: debouncedQuery || undefined,
                    availableOnly: availableOnly ? true : undefined,
                }),
                listBrands(),
            ]);

            setItems(Array.isArray(prods?.items) ? prods.items : []);
            setTotal(Number(prods?.total || 0));
            setBrands(Array.isArray(brs) ? brs : []);
        } catch (e) {
            setErr(e?.message || 'Failed to load products');
        } finally {
            setLoading(false);
        }
    }, [page, limit, sort, dir, brandId, debouncedQuery, availableOnly]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => () => {
        clearDebounceTimer();
        clearAutosaveTimer();
    }, [clearAutosaveTimer, clearDebounceTimer]);

    const flushReorder = useCallback(async () => {
        const pending = pendingReorderRef.current;
        if (!pending) return;
        if (!pending.brandId) return;

        setSavingOrder(true);
        setAutosave('saving');

        try {
            const full = await listAllBrandProducts(pending.brandId);
            const fullIds = uniqueIds(full.map((item) => item.id));
            const idsToSave = buildBrandOrder(fullIds, pending.visibleBeforeIds, pending.visibleAfterIds);

            if (!idsToSave.length) throw new Error('Nothing to save.');

            await reorderProducts(pending.brandId, idsToSave);
            setAutosave('saved');
            clearAutosaveTimer();
            autosaveTimeoutRef.current = setTimeout(() => setAutosave('idle'), 1300);
        } catch (e) {
            setItems(rollbackItemsRef.current);
            setErr(e?.message || 'Failed to save product order');
            setNotice({ open: true, message: e?.message || 'Failed to save order', severity: 'error' });
            setAutosave('error');
        } finally {
            setSavingOrder(false);
            pendingReorderRef.current = null;
        }
    }, [clearAutosaveTimer]);

    const scheduleReorderSave = useCallback((nextItems, previousItems) => {
        if (!brandId || savingOrder) return;

        const nextIds = nextItems.map((item) => String(item.id));
        const previousIds = previousItems.map((item) => String(item.id));

        if (!debounceSaveRef.current) {
            rollbackItemsRef.current = previousItems;
        }

        pendingReorderRef.current = {
            brandId: String(brandId),
            visibleBeforeIds: pendingReorderRef.current?.visibleBeforeIds || previousIds,
            visibleAfterIds: nextIds,
        };

        setAutosave('saving');
        clearDebounceTimer();
        debounceSaveRef.current = setTimeout(() => {
            debounceSaveRef.current = null;
            flushReorder();
        }, 350);
    }, [brandId, clearDebounceTimer, flushReorder, savingOrder]);

    const moveProduct = useCallback((from, to) => {
        if (!reorderEnabled || savingOrder || loading) return;
        if (!brandId) return;
        if (from === to || to < 0 || to >= items.length) return;

        const previous = items;
        const next = reorderList(items, from, to);
        setItems(next);
        scheduleReorderSave(next, previous);
    }, [brandId, items, loading, reorderEnabled, savingOrder, scheduleReorderSave]);

    const onDragEnd = useCallback((result) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.index === destination.index) return;
        moveProduct(source.index, destination.index);
    }, [moveProduct]);

    async function handleRemove(id) {
        if (!window.confirm('Delete product?')) return;

        try {
            await deleteProduct(id);
            setNotice({ open: true, message: 'Product deleted', severity: 'success' });

            if (items.length === 1 && page > 1) {
                setPage((prev) => prev - 1);
                return;
            }

            load();
        } catch (e) {
            setErr(e?.message || 'Failed to delete product');
            setNotice({ open: true, message: e?.message || 'Failed to delete product', severity: 'error' });
        }
    }

    return (
        <ThemeProvider theme={blackIconTheme}>
            <section className={s.page}>
                <header className={s.header}>
                    <div>
                        <h2 className={s.title}>Products</h2>
                        <p className={s.subtitle}>
                            {loading ? 'Loading products...' : `${total} products`} | Global search, edit and cleanup
                        </p>
                    </div>
                    <Link className={s.btnPrimary} to="/products/new">Add product</Link>
                </header>

                <section className={s.filtersCard}>
                    <ProductsToolbar
                        brands={brands}
                        brandId={brandId}
                        onBrandChange={(value) => {
                            setBrandId(value);
                            setPage(1);
                        }}
                        query={query}
                        onQueryChange={setQuery}
                        sort={sort}
                        onSortChange={(value) => {
                            setSort(value);
                            setPage(1);
                        }}
                        dir={dir}
                        onDirChange={(value) => {
                            setDir(value);
                            setPage(1);
                        }}
                        availableOnly={availableOnly}
                        onAvailableOnlyChange={(value) => {
                            setAvailableOnly(value);
                            setPage(1);
                        }}
                    />
                </section>

                {err && <Alert severity="error">{err}</Alert>}

                {loading ? (
                    <div className={s.list}>
                        {Array.from({ length: 8 }).map((_, idx) => (
                            <Skeleton key={idx} variant="rounded" height={76} />
                        ))}
                    </div>
                ) : !items.length ? (
                    <div className={s.emptyState}>
                        <h3>No products found</h3>
                        <p>Try changing filters or create a new product.</p>
                        <Link className={s.btnSecondary} to="/products/new">Create product</Link>
                    </div>
                ) : (
                    <>
                        {!reorderEnabled ? (
                            <Alert severity="info" sx={{ mb: 1 }}>
                                Select a brand to reorder products.
                            </Alert>
                        ) : (
                            <Alert severity="success" sx={{ mb: 1 }}>
                                {autosave === 'saving'
                                    ? 'Saving product order...'
                                    : autosave === 'saved'
                                        ? 'Product order saved'
                                        : 'Reorder enabled for selected brand'}
                            </Alert>
                        )}

                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="products-page-list">
                                {(dropProvided) => (
                                    <div
                                        ref={dropProvided.innerRef}
                                        {...dropProvided.droppableProps}
                                        className={s.list}
                                    >
                                        {items.map((product, index) => (
                                            <Draggable
                                                key={product.id}
                                                draggableId={String(product.id)}
                                                index={index}
                                                isDragDisabled={!reorderEnabled || savingOrder}
                                            >
                                                {(dragProvided) => (
                                                    <div
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        style={dragProvided.draggableProps.style}
                                                    >
                                                        <ProductCard
                                                            product={product}
                                                            brand={product.brandId ? brandById.get(product.brandId) : null}
                                                            onKeyDown={(event) => {
                                                                if (!reorderEnabled || savingOrder || !event.altKey) return;
                                                                if (event.key === 'ArrowUp') {
                                                                    event.preventDefault();
                                                                    moveProduct(index, index - 1);
                                                                }
                                                                if (event.key === 'ArrowDown') {
                                                                    event.preventDefault();
                                                                    moveProduct(index, index + 1);
                                                                }
                                                            }}
                                                            reorderSlot={reorderEnabled ? (
                                                                <>
                                                                    <DragHandle
                                                                        dragHandleProps={dragProvided.dragHandleProps}
                                                                        disabled={savingOrder}
                                                                        label={`Drag ${product.name || 'product'} to reorder`}
                                                                    />
                                                                    <ReorderControls
                                                                        disabled={savingOrder}
                                                                        canMoveUp={index > 0}
                                                                        canMoveDown={index < items.length - 1}
                                                                        onMoveTop={() => moveProduct(index, 0)}
                                                                        onMoveUp={() => moveProduct(index, index - 1)}
                                                                        onMoveDown={() => moveProduct(index, index + 1)}
                                                                        onMoveBottom={() => moveProduct(index, items.length - 1)}
                                                                    />
                                                                </>
                                                            ) : null}
                                                            actionsSlot={(
                                                                <ProductActions
                                                                    editTo={`/products/${product.id}`}
                                                                    onDelete={() => handleRemove(product.id)}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {dropProvided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </>
                )}

                {pages > 1 && (
                    <Pager
                        page={page}
                        pages={pages}
                        onPrev={() => setPage((value) => Math.max(1, value - 1))}
                        onNext={() => setPage((value) => Math.min(pages, value + 1))}
                    />
                )}

                <Snackbar
                    open={notice.open}
                    autoHideDuration={3000}
                    onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
                        severity={notice.severity}
                        sx={{ width: '100%' }}
                    >
                        {notice.message}
                    </Alert>
                </Snackbar>
            </section>
        </ThemeProvider>
    );
}